import { useUnit } from 'effector-react';
import { RotateCcw } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { GameSettingWithGame } from 'shared/api';
import { ConfirmModalProps, createConfirmModal, ModalFormActionIconProps } from 'shared/ui';
import { ResetGameSettingModel } from '../../model';

export const createResetGameSettingModal = (model: ResetGameSettingModel) => {
	const ConfirmModal = createConfirmModal(model);

	const ResetGameSettingModal = forwardRef<HTMLDivElement, ConfirmModalProps>(
		({ ...props }, ref) => {
			const { t } = useTranslation();

			const [setting] = useUnit([model.$modalData]);

			return (
				<ConfirmModal
					ref={ref}
					title={t('reset-game-setting.title')}
					submitButtonProps={{
						variant: 'outline',
						children: t('reset-game-setting.reset'),
					}}
					cancelButtonProps={{
						children: t('reset-game-setting.cancel'),
					}}
					{...props}
				>
					{t('reset-game-setting.description', { name: setting?.game.title })}
				</ConfirmModal>
			);
		},
	);

	const ResetGameSettingModalActionIcon = forwardRef<
		HTMLButtonElement,
		ModalFormActionIconProps<GameSettingWithGame>
	>(({ modalData, ...props }, ref) => {
		const canEditGameSettings = useUnit(viewerModel.$canEditGameSettings);

		return (
			<ConfirmModal.ActionIcon
				ref={ref}
				data-test-id='reset-game-setting-modal-action-icon'
				variant='ghost-secondary'
				disabled={!modalData.isResettable || !canEditGameSettings}
				modalData={modalData}
				{...props}
			>
				<RotateCcw />
			</ConfirmModal.ActionIcon>
		);
	});

	return Object.assign(ResetGameSettingModal, {
		ActionIcon: ResetGameSettingModalActionIcon,
	});
};
